import { t } from '@lingui/macro';
import { ChatRoomProvider, useMessages } from '@ably/chat/react';
import { useQueryClient } from '@tanstack/react-query';
import { useRef, type MutableRefObject } from 'react';
import type {
  MessagingActivityKind,
  MessagingRoomListResponse,
  MessagingRoomSummary,
} from '@jema-manager/shared/types/messaging.types';
import { useDoctorControlStore } from '@shared/stores/doctor-control-store';
import { toast } from 'sonner';
import { notifyWithBrowserEffects } from '@shared/notifications/browserNotificationEffects';
import { messagingQueryKeys } from '../hooks/useMessagingApi';
import { getRoomListTitle } from '../lib/roomDisplay';

type IncomingAblyMessage = {
  serial?: string;
  clientId?: string;
  text?: string;
  createdAt?: number | string | Date;
  timestamp?: number | string | Date;
  metadata?: Record<string, unknown>;
};

const notificationRoomOptions = {
  messages: { rawMessageReactions: false },
};

const getMessageTime = (message: IncomingAblyMessage) => {
  const candidate = message.createdAt ?? message.timestamp;
  if (typeof candidate === 'number') return candidate;
  if (candidate instanceof Date) return candidate.getTime();
  if (typeof candidate === 'string') {
    const parsed = Date.parse(candidate);
    return Number.isNaN(parsed) ? Date.now() : parsed;
  }
  return Date.now();
};

const getSenderId = (message: IncomingAblyMessage) =>
  typeof message.metadata?.senderId === 'string' ? message.metadata.senderId : message.clientId;

const getActivityKind = (message: IncomingAblyMessage): MessagingActivityKind => {
  if (message.metadata?.jemaType === 'attachment') return 'attachment';
  if (message.metadata?.jemaType === 'meeting') return 'meeting';
  return 'message';
};

const getPreviewText = (message: IncomingAblyMessage, room: MessagingRoomSummary, role: string) =>
  message.text?.trim() || getRoomListTitle(room, role);

const sortRoomsByActivity = (rooms: MessagingRoomSummary[]) =>
  [...rooms].sort((first, second) => {
    const firstTime = Date.parse(first.lastActivityAt ?? first.updatedAt ?? first.createdAt);
    const secondTime = Date.parse(second.lastActivityAt ?? second.updatedAt ?? second.createdAt);
    return (Number.isNaN(secondTime) ? 0 : secondTime) - (Number.isNaN(firstTime) ? 0 : firstTime);
  });

function RoomNotificationListener({
  room,
  role,
  messagingUserId,
  seenSerials,
}: {
  room: MessagingRoomSummary;
  role: string;
  messagingUserId: string | null;
  seenSerials: MutableRefObject<Set<string>>;
}) {
  const queryClient = useQueryClient();
  const doctorControlRequestId = useDoctorControlStore(state => state.activeRequest?.id ?? null);

  useMessages({
    listener: (event: { message?: IncomingAblyMessage }) => {
      const message = event.message;
      if (!message?.serial || message.metadata?.jemaType === 'read_receipt') return;

      const senderId = getSenderId(message);
      if (!messagingUserId || !senderId || senderId === messagingUserId) return;
      if (seenSerials.current.has(message.serial)) return;
      seenSerials.current.add(message.serial);

      const preview = getPreviewText(message, room, role);
      const messageAt = new Date(getMessageTime(message)).toISOString();
      const roomsQueryKey = messagingQueryKeys.rooms(doctorControlRequestId);

      queryClient.setQueryData<MessagingRoomListResponse>(roomsQueryKey, current => {
        if (!current) return current;

        return {
          rooms: sortRoomsByActivity(
            current.rooms.map(candidate => {
              if (candidate.id !== room.id) return candidate;
              const previews = [...(candidate.unreadMessagePreviews ?? []), preview].slice(-3);
              return {
                ...candidate,
                lastActivityAt: messageAt,
                lastActivityKind: getActivityKind(message),
                lastMessageAt: messageAt,
                lastMessageSerial: message.serial ?? candidate.lastMessageSerial,
                unreadCountCache: (candidate.unreadCountCache ?? 0) + 1,
                unreadMessagePreviews: previews,
              };
            })
          ),
        };
      });

      toast(t({ id: 'features.messages.toast.newMessage.title', message: 'New message' }), {
        description: preview,
        duration: 4000,
      });
      notifyWithBrowserEffects({
        id: message.serial,
        title: t({ id: 'features.messages.toast.newMessage.title', message: 'New message' }),
        body: preview,
        tag: `room:${room.id}`,
      });
    },
  } as any);

  return null;
}

export function RoomNotificationSubscriptions({
  rooms,
  selectedRoomId,
  role,
  messagingUserId,
}: {
  rooms: MessagingRoomSummary[];
  selectedRoomId?: string | null;
  role: string;
  messagingUserId: string | null;
}) {
  const seenSerials = useRef<Set<string>>(new Set());

  return (
    <>
      {rooms
        .filter(room => room.id !== selectedRoomId)
        .map(room => (
          <ChatRoomProvider key={room.id} name={room.ablyRoomId} options={notificationRoomOptions}>
            <RoomNotificationListener
              room={room}
              role={role}
              messagingUserId={messagingUserId}
              seenSerials={seenSerials}
            />
          </ChatRoomProvider>
        ))}
    </>
  );
}
